import { signInWithCustomToken, type User } from "firebase/auth";
import { auth } from "@/services/firebaseService";
import { enforceProAccessGate } from "@/lib/proAccessGate";
import { mintApexSession } from "@/lib/apexSessionMint";
import { recordApexSessionObserved } from "@/lib/apexObservedState";
import { APEX_CUSTOM_TOKEN_URL } from "@/lib/apexCustomTokenEndpoint";
import { emitAnalyticsEvent } from "@/lib/analytics/emitEvent";
import { ANALYTICS_EVENTS } from "@/lib/analytics/eventNames";

/**
 * KB-3228 — adopt an existing kidsbooked.com apex session as a FIRST-CLASS GATED
 * DOOR into pro, not a bypass.
 *
 * A parent signed in on `kidsbooked.com` who follows a link into pro should land
 * signed in — but ONLY if their role is on the pro allowlist. This runs the same
 * `enforceProAccessGate` every other sign-in door runs (`proAccessGate.ts`); it
 * is the FIFTH such door. Ungated, it would hand a pro session to every
 * kidsbooked.com consumer, which is exactly the harm KB-3133 exists to close.
 *
 * The one difference from the interactive doors is the rejection: it is SILENT
 * (`SILENT_REJECTION_DOORS` in `proAccessGate.ts`), because this caller clicked a
 * link and never asked to sign in here. The gate still RUNS and still signs a
 * non-allowlisted caller out; only the toast is withheld.
 *
 * ## Why signInWithCustomToken is NOT bounded, while the fetch IS
 *
 * The credentialed fetch is bounded (`APEX_SSO_FETCH_BUDGET_MS`, AbortController)
 * so a hung apex endpoint cannot keep this promise pending. `signInWithCustomToken`
 * is deliberately NOT wrapped in `boundAuthCall`. A bound abandons the promise on
 * expiry (promises are not cancellable — see `auth-transport.ts`), and a
 * `signInWithCustomToken` that resolves AFTER its bound would fire
 * `onAuthStateChanged(user)` with the gate never having run — an ungated pro
 * session, the precise bypass KB-3133 forbids. Awaiting it directly means the
 * gate ALWAYS runs after any successful sign-in, whenever it settles. Liveness of
 * the "Loading your session…" gate does not depend on this bound at all: the
 * 8000ms `BOOTSTRAP_BUDGET_MS` timer in `AuthContext.tsx` releases the gate
 * regardless (AC-6), and a hung sign-in creates no session, so it grants no
 * access.
 */

/**
 * The apex session-cookie → custom-token exchange (CORS-wired by KB-3227). The
 * literal moved to the dependency-free leaf `apexCustomTokenEndpoint.ts` (KB-3271,
 * landmine L379) so `apexSessionChecker.ts` can share it without reading it through
 * this mockable behaviour module at module scope. Re-exported here for back-compat
 * with existing importers (e.g. `apexSsoBootstrap.test.ts`).
 */
export { APEX_CUSTOM_TOKEN_URL };

/**
 * How long to wait on the apex fetch before giving up and rendering the login
 * page. Comfortably under `BOOTSTRAP_BUDGET_MS` (8000ms) so, on a slow-but-alive
 * apex, the attempt still resolves before the gate's own backstop fires.
 */
export const APEX_SSO_FETCH_BUDGET_MS = 5000;

/**
 * What the bootstrap attempt resolved to. `allowed` means a pro session is now
 * live (the caller lands in the app); every other value ends with the caller on
 * the login page. `denied`/`unverifiable` are kept distinct from `no-session`/
 * `error` so the caller and tests can tell "the gate refused a real apex user"
 * apart from "there was no apex session to adopt".
 */
export type ApexSsoOutcome =
  | "allowed"
  | "denied"
  | "unverifiable"
  | "no-session"
  | "error";

/** Response body of the custom-token endpoint (KB-1108 / KB-3227). */
interface CustomTokenResponse {
  token: string | null;
  reason?: string;
}

/**
 * GET the apex custom token with the session cookie. RETURNS the token string,
 * or `null` for the NORMAL signed-out outcome — the endpoint answers 200-with-null
 * rather than 401 precisely so this case is legible (KB-1108). A `null` token, a
 * non-string token, and a non-2xx response all RETURN `null` (→ silent
 * `no-session`).
 *
 * It does NOT catch its own failures: an aborted/slow fetch (the AbortController
 * timeout) or a non-JSON `res.json()` body THROWS past the `finally` and is caught
 * by the caller, which maps it to `'error'` + a `console.warn` — NOT `no-session`.
 * Either way the honest render is the login page; the only difference is which
 * outcome the caller reports.
 */
async function fetchApexCustomToken(): Promise<string | null> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), APEX_SSO_FETCH_BUDGET_MS);
  try {
    const res = await fetch(APEX_CUSTOM_TOKEN_URL, {
      method: "GET",
      credentials: "include",
      signal: controller.signal,
    });
    if (!res.ok) return null;
    const body = (await res.json()) as CustomTokenResponse | null;
    const token = body?.token;
    return typeof token === "string" && token.length > 0 ? token : null;
  } finally {
    clearTimeout(timer);
  }
}

/**
 * Attempt to sign in from the apex session and gate the result. Safe to call at
 * most once per page session (a rejection signs the caller back out, which
 * re-fires the auth listener — the caller must not re-enter). See
 * `AuthContext.tsx`'s `ssoBootstrapAttemptedRef`.
 */
export async function attemptApexSsoBootstrap(): Promise<ApexSsoOutcome> {
  let token: string | null;
  try {
    token = await fetchApexCustomToken();
  } catch (fetchError) {
    // A genuine network/parse failure — distinct from the silent null path, so a
    // warn (not an error, to keep it out of Sentry noise) aids debugging. No
    // toast: the login page is the honest surface, and it asserts nothing about
    // the apex session it could not reach.
    console.warn("Apex SSO bootstrap: custom-token fetch failed:", fetchError);
    return "error";
  }

  if (!token) {
    // The normal signed-out outcome. Completely silent — no console output, no
    // toast (AC-7).
    return "no-session";
  }

  let user: User;
  try {
    const credential = await signInWithCustomToken(auth, token);
    user = credential.user;
  } catch (signInError) {
    console.warn("Apex SSO bootstrap: signInWithCustomToken failed:", signInError);
    return "error";
  }

  // ⚠️ EMIT BEFORE THE GATE CALL (AC-5). `enforceProAccessGate` signs a refused
  // caller out, which clears `auth.currentUser`; the KB-2611 internal sink
  // early-returns on `!auth.currentUser` (`internalSink.ts`), so an attempt event
  // emitted after the gate would reach GA4 and reach the internal sink never.
  // Emitted here — after the sign-in, so `currentUser` is set — it reaches both.
  // The gate emits its own AUTH_LOGIN_FAILURE on a refusal, also before its
  // sign-out.
  emitAnalyticsEvent(ANALYTICS_EVENTS.AUTH_LOGIN_SUBMIT, { method: "apex-sso" });

  const verdict = await enforceProAccessGate(user.uid, "apex-sso");
  if (verdict.outcome === "allowed") {
    // KB-3271 AC-1: we adopted a REAL, live apex session for this uid — arm the
    // reverse checker. Recorded here independently of the mint below: the mint is
    // fire-and-forget and can fail silently, but the apex session we just adopted
    // is real regardless, so this must not depend on the mint's outcome.
    recordApexSessionObserved(user.uid);
    // AC-3/AC-4: mint the apex cookie ONLY now that the gate has allowed this
    // caller — and it runs on this door too, so an adopted apex session is not
    // left un-refreshed. Fire-and-forget: not awaited, so a failed or slow mint
    // cannot delay landing the caller in the app (AC-2). See `apexSessionMint.ts`.
    void mintApexSession(user);
    emitAnalyticsEvent(ANALYTICS_EVENTS.AUTH_LOGIN_SUCCESS, { method: "apex-sso" });
    return "allowed";
  }

  // denied | unverifiable: the gate has already signed the caller out and
  // recorded the refusal; on this silent door it showed nothing. Return the
  // outcome so it stays distinguishable.
  return verdict.outcome;
}
